import { uniqueId } from 'lodash'
import React, { FC, useMemo } from 'react'
import { secToHourMinute } from '../../../utils/date'

interface Props {
  list: {
    label: string
    value: number
    color?: string
  }[]
  chartSize: number
  color?: string
  strongColor?: string
  type?: string
  gap?: boolean
  horizontal?: boolean
  ruler?: boolean
  scale?: boolean
  labelSkip?: number
  vLabelLimit?: number
}

const BarChart: FC<Props> = (props) => {
  const {
    list,
    chartSize,
    color,
    strongColor,
    type,
    gap,
    horizontal,
    ruler,
    scale,
    labelSkip,
    vLabelLimit,
  } = props

  const V_LABEL_LIMIT = 3

  const maxValue = useMemo(() => (
    Math.max(...list.map((value) => value.value))
  ), [list])

  const buildUnit = (value: number) => {
    if (!value) return <></>
    if (type === "person") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          人
        </>
      )
    }
    if (type === "biz") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          biz
        </>
      )
    }
    if (type === "page") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          枚
        </>
      )
    }
    if (type === "time") {
      return (
        <span>
          {secToHourMinute(value)}
        </span>
      )
    }
    if (type === "times") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          回
        </>
      )
    }
    if (type === "percent") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          %
        </>
      )
    }
    return (
      <></>
    )
  }

  const buildLabel = (label: string) => {
    let fontColor = ""
    if (label === "土") {
      fontColor = "#317ab7"
    } else if (label === "日") {
      fontColor = "#ff0000"
    }
    return (
      <span
        style={{ color: fontColor }}
      >
        {label}
      </span>
    )
  }

  const buildHorizontalBarChart = () => {
    const max = Math.max(...list.map((value) => value.value))

    return list.map((value) => (
      <div
        className="row"
        style={{
          padding: gap ? "8px 0" : "0",
        }}
        key={`bar_chart_row_${value.label}`}
      >
        <div className="row_title">
          {buildLabel(value.label)}
        </div>
        <div className="row_value">
          <div
            className="stacked_bar_chart"
            style={{
              width: `${(value.value / (max * 1.05)) * 100}%`,
              backgroundColor: value.color || (value.value === max ? strongColor : color),
            }}
          >
            <div
              className="label"
              style={{
                color: value.color || (value.value === max ? strongColor : color),
              }}
            >
              {buildUnit(value.value)}
            </div>
          </div>
        </div>
      </div>
    ))
  }

  const buildVerticalBarChart = () => {
    const max = Math.max(...list.map((value) => value.value))

    return list.map((value, index) => (
      <div
        className="column_value"
        style={{
          height: `${chartSize}px`,
          width: `calc(100% / ${list.length || 1})`,
        }}
        key={`column_value_${value.label}`}
      >
        <div
          className="stacked_bar_chart"
          style={{
            width: gap ? "80%" : "100%",
            height: `${(value.value / (max * 1.05)) * 100}%`,
            backgroundColor: value.color || (value.value === max ? strongColor : color),
            borderRight: gap ? "1px solid #FFF" : "",
            borderLeft: gap ? "1px solid #FFF" : "",
          }}
        >
          {
            !ruler && type ? (
              <div className="label">
                {buildUnit(value.value)}
              </div>
            ) : (
              <></>
            )
          }
          {
            ruler ? (
              <div className="ruler_border" />
            ) : (
              <></>
            )
          }
        </div>
        {
          labelSkip && (index % labelSkip) !== 0 ? (
            <></>
          ) : (
            <div className="column_title">
              {buildLabel(value.label)}
            </div>
          )
        }
      </div>
    ))
  }

  return (
    <div className="bar_chart_wrap">
      {
        horizontal ? (
          <div className="horizontal">
            <div className="bar_chart_area">
              {buildHorizontalBarChart()}
            </div>
          </div>
        ) : (
          <div className={`vertical ${(ruler && type) ? "ruler" : ""}`}>
            <div
              className="bar_chart_area"
              style={{
                height: `${chartSize}px`,
                justifyContent: gap ? "space-evenly" : "center",
              }}
            >
              {
                scale && Array((vLabelLimit || V_LABEL_LIMIT)).fill(null).map((_v, i) => (
                  <div
                    className="scale"
                    style={{
                      top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                    }}
                    key={uniqueId("ruler")}
                  />
                ))
              }
              {buildVerticalBarChart()}
            </div>
            {
              ruler ? (
                <div className="ruler_area">
                  <div
                    className="horizontal_ruler_area"
                    style={{
                      top: `${chartSize}px`,
                    }}
                  />
                  {
                    type ? (
                      <div className="vertical_ruler_area">
                        <div className="label_area">
                          {
                            Array((vLabelLimit || V_LABEL_LIMIT)).fill(null).map((_v, i) => (
                              <React.Fragment key={uniqueId("ruler")}>
                                <div
                                  className="scale"
                                  style={{
                                    top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                                  }}
                                />
                                <div
                                  className="label"
                                  style={{
                                    top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                                  }}
                                >
                                  {buildUnit(Math.floor((maxValue / (vLabelLimit || V_LABEL_LIMIT)) * ((vLabelLimit || V_LABEL_LIMIT) - i)))}
                                </div>
                              </React.Fragment>
                            ))
                          }
                        </div>
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
              ) : (
                <></>
              )
            }
          </div>
        )
      }
    </div>
  )
}

BarChart.defaultProps = {
  color: "#666666",
  strongColor: "#000000",
  type: "",
  gap: true,
  horizontal: false,
  ruler: false,
  scale: false,
  labelSkip: 0,
  vLabelLimit: 3,
}

export default BarChart
